/**
 * PaymentStatusBadge Component
 * Visual status indicators for payments and invoices
 */

import { Badge } from '../common/Badge';
import {
  Clock, Settings, CheckCircle2, XCircle, CornerUpLeft,
  FileEdit, FileText, Mail, AlertTriangle,
  CreditCard, Building2, Smartphone, Banknote
} from 'lucide-react';
import type { PaymentStatus, InvoiceStatus } from '../../types/payment';

export interface PaymentStatusBadgeProps {
  status: PaymentStatus | InvoiceStatus;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  className?: string;
}

export function PaymentStatusBadge({
  status,
  size = 'md',
  showIcon = true,
  className = '',
}: PaymentStatusBadgeProps) {
  const statusConfig: Record<
    PaymentStatus | InvoiceStatus,
    {
      label: string;
      variant: 'success' | 'warning' | 'error' | 'info' | 'default' | 'primary';
      icon?: React.ReactNode;
    }
  > = {
    // Payment statuses
    PENDING: {
      label: 'Pending',
      variant: 'warning',
      icon: <Clock className="w-3 h-3" />,
    },
    PROCESSING: {
      label: 'Processing',
      variant: 'info',
      icon: <Settings className="w-3 h-3" />,
    },
    COMPLETED: {
      label: 'Completed',
      variant: 'success',
      icon: <CheckCircle2 className="w-3 h-3" />,
    },
    FAILED: {
      label: 'Failed',
      variant: 'error',
      icon: <XCircle className="w-3 h-3" />,
    },
    REFUNDED: {
      label: 'Refunded',
      variant: 'default',
      icon: <CornerUpLeft className="w-3 h-3" />,
    },
    // Invoice statuses
    DRAFT: {
      label: 'Draft',
      variant: 'default',
      icon: <FileEdit className="w-3 h-3" />,
    },
    ISSUED: {
      label: 'Issued',
      variant: 'primary',
      icon: <FileText className="w-3 h-3" />,
    },
    SENT: {
      label: 'Sent',
      variant: 'info',
      icon: <Mail className="w-3 h-3" />,
    },
    PAID: {
      label: 'Paid',
      variant: 'success',
      icon: <CheckCircle2 className="w-3 h-3" />,
    },
    OVERDUE: {
      label: 'Overdue',
      variant: 'error',
      icon: <AlertTriangle className="w-3 h-3" />,
    },
    CANCELLED: {
      label: 'Cancelled',
      variant: 'default',
      icon: <XCircle className="w-3 h-3" />,
    },
  };

  const config = statusConfig[status];

  return (
    <Badge variant={config.variant} size={size} className={className}>
      {showIcon && config.icon && <span className="mr-1">{config.icon}</span>}
      {config.label}
    </Badge>
  );
}

/**
 * PaymentMethodBadge Component
 * Display payment method with icon
 */
export interface PaymentMethodBadgeProps {
  method: string; // PaymentMethod enum value
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function PaymentMethodBadge({
  method,
  size = 'md',
  className = '',
}: PaymentMethodBadgeProps) {
  const methodConfig: Record<
    string,
    {
      label: string;
      icon: React.ReactNode;
    }
  > = {
    CREDIT_CARD: {
      label: 'Credit Card',
      icon: <CreditCard className="w-3 h-3" />,
    },
    DEBIT_CARD: {
      label: 'Debit Card',
      icon: <CreditCard className="w-3 h-3" />,
    },
    ONLINE_BANKING: {
      label: 'Online Banking',
      icon: <Building2 className="w-3 h-3" />,
    },
    E_WALLET: {
      label: 'E-Wallet',
      icon: <Smartphone className="w-3 h-3" />,
    },
    BANK_TRANSFER: {
      label: 'Bank Transfer',
      icon: <Building2 className="w-3 h-3" />,
    },
    CASH: {
      label: 'Cash',
      icon: <Banknote className="w-3 h-3" />,
    },
    CHEQUE: {
      label: 'Cheque',
      icon: <FileEdit className="w-3 h-3" />,
    },
  };

  const config = methodConfig[method] || {
    label: method,
    icon: <CreditCard className="w-3 h-3" />,
  };

  return (
    <Badge variant="default" size={size} className={className}>
      <span className="mr-1">{config.icon}</span>
      {config.label}
    </Badge>
  );
}

/**
 * PaymentPurposeBadge Component
 * Display payment purpose/type
 */
export interface PaymentPurposeBadgeProps {
  purpose: string; // PaymentPurpose enum value
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function PaymentPurposeBadge({
  purpose,
  size = 'md',
  className = '',
}: PaymentPurposeBadgeProps) {
  const purposeConfig: Record<
    string,
    {
      label: string;
      variant: 'success' | 'warning' | 'error' | 'info' | 'default' | 'primary';
    }
  > = {
    APPLICATION_FEE: {
      label: 'Application Fee',
      variant: 'primary',
    },
    CONSULTATION_FEE: {
      label: 'Consultation',
      variant: 'info',
    },
    AUDIT_FEE: {
      label: 'Audit Fee',
      variant: 'primary',
    },
    CERTIFICATION_FEE: {
      label: 'Certification',
      variant: 'success',
    },
    TRAINING_FEE: {
      label: 'Training',
      variant: 'info',
    },
    RENEWAL_FEE: {
      label: 'Renewal',
      variant: 'warning',
    },
    SURVEILLANCE_FEE: {
      label: 'Surveillance',
      variant: 'info',
    },
    DOCUMENT_FEE: {
      label: 'Documents',
      variant: 'default',
    },
    OTHER: {
      label: 'Other',
      variant: 'default',
    },
  };

  const config = purposeConfig[purpose] || {
    label: purpose,
    variant: 'default' as const,
  };

  return (
    <Badge variant={config.variant} size={size} className={className}>
      {config.label}
    </Badge>
  );
}
